import type { ReactElement } from 'react';
import { CarSearch } from 'shared/search/car/CarSearch';
import { FhhSearch } from 'shared/search/fhh/FhhSearch';
import { FlightSearch } from 'shared/search/flight/FlightSearch';
import { HotelSearch } from 'shared/search/hotel/HotelSearch';
import { PrepackageSearch } from 'shared/search/prepackage/PrepackageSearch';
import type { SeoData } from 'shared/seo/interfaces';
import type { CityPlace, CountryPlace } from '@/interfaces/place';

export interface LevelFactory {
    pageValues(): {
        pagePath: string;
        origin?: {
            display: string;
        };
        destination: Pick<CityPlace, 'continent' | 'image' | 'price' | 'facts'> & { display: string };
    };

    breadcrumbList(): Array<ReactElement>;

    menuValues(): {
        shortText: string;
        SearchComponent(): ReactElement;
    };

    structuredDataValues(path: string): {
        breadcrumbList: Array<unknown>;
        destination: string;
        price: number;
    };
}

type SearchComponentFactory = (prefill: Prefill) => ReactElement;

type Destination = {
    shortText: string;
    longText: string;
    city?: string;
    country: string;
    airport: string;
    searchable: boolean;
    placeKey: string;
    hierarchy: string[];
    type: 'country' | 'place';
    locationCodes: string[];
    location?: {
        id: number;
        text: string;
        latitude: string;
        longitude: string;
    };
};

type Prefill = {
    origin: {
        longText?: string;
        city?: string;
        country?: string;
        airport?: string;
    };
    destination: Destination;
};

export const flightSearchFactory = (): SearchComponentFactory => (prefill: Prefill) => (
    <FlightSearch
        prefill={{
            origin: prefill.origin.airport,
            originName: prefill.origin.longText,
            originCity: prefill.origin.city,
            originCountry: prefill.origin.country,
            destination: prefill.destination.airport,
            destinationName: prefill.destination.longText,
            destinationCity: prefill.destination.city,
            destinationCountry: prefill.destination.country,
        }}
        position="destinationpages"
    />
);

export const hotelSearchFactory = (): SearchComponentFactory => (prefill: Prefill) => (
    <HotelSearch
        prefill={
            prefill.destination.searchable
                ? {
                      destination: prefill.destination.longText,
                      destinationContext: {
                          text: prefill.destination.shortText,
                          key: prefill.destination.placeKey,
                          searchable: true,
                          hierarchy: prefill.destination.hierarchy,
                          type: prefill.destination.type,
                      },
                  }
                : undefined
        }
        position="destinationpages"
    />
);

export const prepackageSearchFactory = (): SearchComponentFactory => (prefill: Prefill) => (
    <PrepackageSearch
        prefill={{
            destination: prefill.destination.longText,
            destinationId: prefill.destination.locationCodes[0],
        }}
        position="destinationpages"
    />
);

export const vacationRentalSearchFactory = (): SearchComponentFactory => (prefill: Prefill) => (
    <FhhSearch
        prefill={{
            destination: prefill.destination.longText,
            destinationData: { text: prefill.destination.longText },
        }}
        position="destinationpages"
    />
);

export const carRentalSearchFactory = (): SearchComponentFactory => (prefill: Prefill) => (
    <CarSearch
        prefill={
            prefill.destination.location
                ? {
                      pickupLocation: prefill.destination.location.text,
                      pickupLocationId: prefill.destination.location.id,
                      pickupLocationLatitude: prefill.destination.location.latitude,
                      pickupLocationLongitude: prefill.destination.location.longitude,
                  }
                : undefined
        }
        position="destinationpages"
    />
);

const fromCountryPlace = (destination: CountryPlace): Destination => ({
    shortText: destination.country,
    longText: destination.country,
    city: null,
    country: destination.country,
    airport: destination.airport,
    searchable: destination.searchable,
    placeKey: destination.placeKey,
    hierarchy: [destination.country],
    type: 'country',
    locationCodes: destination.locationCodes,
    location: {
        id: destination.location?.id,
        text: destination.location?.text,
        latitude: destination.location?.latitude,
        longitude: destination.location?.longitude,
    },
});

const fromCityPlace = (destination: CityPlace): Destination => ({
    shortText: destination.city,
    longText: `${destination.city}, ${destination.country}`,
    city: destination.city,
    country: destination.country,
    airport: destination.airport,
    searchable: destination.searchable,
    placeKey: destination.placeKey,
    hierarchy: [destination.country, destination.city],
    type: 'place',
    locationCodes: destination.locationCodes,
    location: {
        id: destination.location?.id,
        text: destination.location?.text,
        latitude: destination.location?.latitude,
        longitude: destination.location?.longitude,
    },
});

export const countryLevel1Factory = (destination: CountryPlace, searchComponentFactory: SearchComponentFactory): LevelFactory => ({
    pageValues: () => ({
        pagePath: `${destination.countryPath}/`,
        destination: {
            display: destination.country,
            continent: destination.continent,
            image: destination.image,
            price: destination.price,
        },
    }),
    breadcrumbList: () => [<span key="breadcrumb3">{destination.country}</span>],
    menuValues: () => ({
        shortText: destination.country,
        SearchComponent: () =>
            searchComponentFactory({
                origin: {},
                destination: fromCountryPlace(destination),
            }),
    }),
    structuredDataValues: (path: string) => ({
        breadcrumbList: [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `https://www.finn.no${path}${destination.countryPath}/`,
                    '@type': 'webpage',
                    name: destination.country,
                },
            },
        ],
        destination: destination.country,
        price: destination.price,
    }),
});

export const cityLevel1Factory = (
    destination: CityPlace,
    seoData: SeoData,
    searchComponentFactory: SearchComponentFactory,
): LevelFactory => ({
    pageValues: () => ({
        pagePath: `${destination.countryPath}/${destination.cityPath}/`,
        destination: {
            display: destination.city,
            continent: destination.continent,
            image: destination.image,
            price: destination.price,
        },
    }),
    breadcrumbList: () => [
        <a href={`${seoData.basePath}${destination.countryPath}/`} key="breadcrumb3">
            {destination.country}
        </a>,
        <span key="breadcrumb4">{destination.city}</span>,
    ],
    menuValues: () => ({
        shortText: destination.city,
        SearchComponent: () =>
            searchComponentFactory({
                origin: {},
                destination: fromCityPlace(destination),
            }),
    }),
    structuredDataValues: (path: string) => ({
        breadcrumbList: [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `https://www.finn.no${path}${destination.countryPath}/`,
                    '@type': 'webpage',
                    name: destination.country,
                },
            },
            {
                '@type': 'ListItem',
                position: 3,
                item: {
                    '@id': `https://www.finn.no${path}${destination.countryPath}/${destination.cityPath}/`,
                    '@type': 'webpage',
                    name: destination.city,
                },
            },
        ],
        destination: destination.city,
        price: destination.price,
    }),
});

export const countryLevel2Factory = (
    origin: CityPlace,
    destination: CountryPlace,
    seoData: SeoData,
    searchComponentFactory: SearchComponentFactory,
): LevelFactory => ({
    pageValues: () => ({
        pagePath: `${origin.countryPath}/${origin.cityPath}/${destination.countryPath}`,
        origin: {
            display: origin.city,
        },
        destination: {
            display: destination.country,
            continent: destination.continent,
            image: destination.image,
            price: destination.price,
        },
    }),
    breadcrumbList: () => [
        <a href={`${seoData.basePath}${origin.countryPath}/`} key="breadcrumb3">
            {origin.country}
        </a>,
        <a href={`${seoData.basePath}${origin.countryPath}/${origin.cityPath}/`} key="breadcrumb4">
            {origin.city}
        </a>,
        <span key="breadcrumb5">{destination.country}</span>,
    ],
    menuValues: () => ({
        shortText: destination.country,
        SearchComponent: () =>
            searchComponentFactory({
                origin: {
                    longText: origin.country,
                    city: origin.city,
                    country: origin.country,
                    airport: origin.airport,
                },
                destination: fromCountryPlace(destination),
            }),
    }),
    structuredDataValues: (path: string) => ({
        breadcrumbList: [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `https://www.finn.no${path}${destination.countryPath}/`,
                    '@type': 'webpage',
                    name: destination.country,
                },
            },
        ],
        destination: destination.country,
        price: destination.price,
    }),
});

export const cityLevel2Factory = (
    origin: CityPlace,
    destination: CityPlace,
    seoData: SeoData,
    searchComponentFactory: SearchComponentFactory,
): LevelFactory => ({
    pageValues: () => ({
        pagePath: `${origin.countryPath}/${origin.cityPath}/${destination.countryPath}/${destination.cityPath}/`,
        origin: {
            display: origin.city,
        },
        destination: {
            display: destination.city,
            continent: destination.continent,
            image: destination.image,
            price: destination.price,
        },
    }),
    breadcrumbList: () => [
        <a href={`${seoData.basePath}${origin.countryPath}/`} key="breadcrumb3">
            {origin.country}
        </a>,
        <a href={`${seoData.basePath}${origin.countryPath}/${origin.cityPath}/`} key="breadcrumb4">
            {origin.city}
        </a>,
        <span key="breadcrumb5">{destination.country}</span>,
        <span key="breadcrumb6">{destination.city}</span>,
    ],
    menuValues: () => ({
        shortText: destination.city,
        SearchComponent: () =>
            searchComponentFactory({
                origin: {
                    longText: `${origin.city}, ${origin.country}`,
                    city: origin.city,
                    country: origin.country,
                    airport: origin.airport,
                },
                destination: fromCityPlace(destination),
            }),
    }),
    structuredDataValues: (path: string) => ({
        breadcrumbList: [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `https://www.finn.no${path}${origin.countryPath}/`,
                    '@type': 'webpage',
                    name: origin.country,
                },
            },
            {
                '@type': 'ListItem',
                position: 3,
                item: {
                    '@id': `https://www.finn.no${path}${origin.countryPath}/${origin.cityPath}/`,
                    '@type': 'webpage',
                    name: origin.city,
                },
            },
            /* Enable this when we have any city to country content
            {
                '@type': 'ListItem',
                position: 4,
                item: {
                    '@id': `https://www.finn.no${path}${origin.countryPath}/${origin.cityPath}/${destination.countryPath}/`,
                    '@type': 'webpage',
                    name: destination.country,
                },
            },
*/
            {
                '@type': 'ListItem',
                position: 4,
                // position: 5, Use this when we have any city to country content
                item: {
                    '@id': `https://www.finn.no${path}${origin.countryPath}/${origin.cityPath}/${destination.countryPath}/${destination.cityPath}/`,
                    '@type': 'webpage',
                    name: destination.city,
                },
            },
        ],
        destination: destination.city,
        price: destination.price,
    }),
});
